import { zodResolver } from '@hookform/resolvers/zod';
import {
  BulkQuoteOfferingReqSchema,
  IBulkOfferingItem,
  IBulkQuoteOfferingReqSchema,
  IOfferingRes,
  IQuoteItemReqSchema,
  IQuoteOfferingReqSchema,
  IQuoteOfferingRespSchema,
  IQuoteRespSchema,
} from '@monetize/types/app';
import { pluralize } from '@monetize/utils/core';
import isEmpty from 'lodash/isEmpty';
import { ReactNode, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { handleApiErrorToast } from '../../../../../api/axios';
import { MText } from '../../../../../components/Monetize';
import { quoteOfferingInitial } from '../../../../../constants/quotes';
import { QuoteDataTypes } from '../../../../../hooks/useQuote';
import { logger } from '../../../../../services/logger';
import {
  BulkOfferingAddStatusHeader,
  BulkOfferingAddStatusList,
} from './BulkOfferingAddStatusList';
import { BulkReviewRates } from './BulkReviewRates';
import { useBulkOfferingAddRun } from './hook/useBulkOfferingAddRun';
import { BulkOfferingSelection } from './OfferingSelection';
import { StepperComponent, StepperComponentRef } from './StepperComponent';
import { getCachedBillingFrequency } from './utils/billingFrequencyCacheUtil';
import {
  areAllOfferingsOneTime,
  BulkRateEntry,
  buildBulkOfferingItems,
  getCustomFrequencyError,
  resolveBulkRates,
} from './utils/bulkOfferingUtils';

export type BulkOfferingItemQuantityHandlerProps = {
  rateId: string;
  items: IQuoteItemReqSchema[];
};

const getAddLabel = (count: number) =>
  `Add ${count} ${pluralize('offering', count)}`;

export const BulkOfferingFlow = ({
  items,
  disabled,
  isBulkOfferingLoading,
  quote,
  quoteData,
  displayImportOffering,
  setIsBulkOfferingLoading,
  onSubmit,
  handleInitialQuoteItemQuantityForBulkOffering,
  fetchQuote,
}: {
  items: IOfferingRes[];
  disabled?: boolean;
  isBulkOfferingLoading: boolean;
  quote: IQuoteRespSchema;
  quoteData: QuoteDataTypes;
  displayImportOffering: boolean;
  setIsBulkOfferingLoading: (loadingState: boolean) => void;
  /** Resolves to the created offering, or to nothing when it was not created */
  onSubmit: ({
    data,
    doUpdateParent,
  }: {
    data: IQuoteOfferingReqSchema;
    doUpdateParent: boolean;
  }) => Promise<IQuoteOfferingRespSchema | null | undefined>;
  handleInitialQuoteItemQuantityForBulkOffering: ({
    rateId,
    items,
  }: BulkOfferingItemQuantityHandlerProps) => Promise<
    {
      productId: string;
      quantity: number;
      incrementalQuantity?: number | null;
    }[]
  >;
  fetchQuote: (
    quoteId: string,
    modifyQuoteResp?: (quote: IQuoteRespSchema) => IQuoteRespSchema,
  ) => Promise<IQuoteRespSchema | null>;
}) => {
  const bulkOfferingCreationState = useForm<IBulkQuoteOfferingReqSchema>({
    resolver: zodResolver(BulkQuoteOfferingReqSchema),
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      offerings: [],
      billingFrequency: '',
      customBillingFrequency: null,
      bulkRate: [],
    },
  });
  const {
    control,
    trigger,
    getValues,
    setValue,
    watch,
    handleSubmit,
    reset,
    formState: { errors },
  } = bulkOfferingCreationState;

  const watchOfferings = watch('offerings');
  const watchBillingFrequency = watch('billingFrequency');
  const watchCustomBillingFreq = watch('customBillingFrequency');
  const bulkRateWatch = watch('bulkRate');

  // Ref to control stepper from outside
  const stepperRef = useRef<StepperComponentRef>(null);

  const { addStatuses, runSize, runAdd, clearAddStatuses } =
    useBulkOfferingAddRun();

  /**
   * Creates one offering through the placeholder row's `onSubmit`, so customer
   * automations and group membership apply as they do for a single add.
   *
   * @returns whether the offering was created
   */
  const addBulkOffering = async (
    { offeringId, rateId, productIds }: BulkRateEntry,
    offerings: IBulkOfferingItem[],
  ) => {
    const offering = offerings.find(({ id }) => id === offeringId);
    const itemsWithQuantity =
      await handleInitialQuoteItemQuantityForBulkOffering({
        rateId,
        items: buildBulkOfferingItems(offering?.offeringProducts, productIds),
      });

    const offeringRes = await onSubmit({
      data: {
        ...quoteOfferingInitial,
        offeringId,
        rateId,
        items: itemsWithQuantity as IQuoteItemReqSchema[],
      },
      doUpdateParent: false,
    });
    // A failed create resolves to null, and an early return to undefined
    return !!offeringRes;
  };

  const resetFlow = (resetStepper?: () => void) => {
    resetStepper?.();
    reset();
    clearAddStatuses();
  };

  const addOfferings = async ({
    data,
    entries,
    resetStepper,
  }: {
    data: IBulkQuoteOfferingReqSchema;
    entries: BulkRateEntry[];
    resetStepper?: () => void;
  }) => {
    setIsBulkOfferingLoading(true);
    // A retry leaves unconfirmed rows out, and they still need a look
    const hasUnconfirmedRows =
      Object.values(addStatuses).includes('unconfirmed');

    let notAddedOfferingIds: string[] = [];
    try {
      notAddedOfferingIds = await runAdd(entries, (entry) =>
        addBulkOffering(entry, data.offerings),
      );
      if (quote?.id) {
        await fetchQuote(quote.id);
      }
    } catch (error) {
      handleApiErrorToast(error);
      logger.error('ERROR onBulkOfferingSubmit', error);
    } finally {
      setIsBulkOfferingLoading(false);
    }

    // Rows that were not added stay on screen
    if (!notAddedOfferingIds.length && !hasUnconfirmedRows) {
      resetFlow(resetStepper);
    }
  };

  const handleAddSubmit = (_: unknown, resetStepper?: () => void) => {
    handleSubmit((data) =>
      addOfferings({ data, entries: data.bulkRate, resetStepper }),
    )();
  };

  // Only `failed` rows are retried: an `unconfirmed` one may already exist
  const handleRetrySubmit = (_: unknown, resetStepper?: () => void) => {
    handleSubmit((data) =>
      addOfferings({
        data,
        entries: data.bulkRate.filter(
          ({ offeringId }) => addStatuses[offeringId] === 'failed',
        ),
        resetStepper,
      }),
    )();
  };

  // Handler for onApply - triggers the next button action
  const handleOnApply = () => {
    if (watchOfferings.length > 0) {
      stepperRef.current?.triggerNext();
    }
  };

  const isAddRunStarted = !isEmpty(addStatuses);
  const failedCount = Object.values(addStatuses).filter(
    (status) => status === 'failed',
  ).length;
  const missingRateCount = bulkRateWatch.filter(
    ({ rateId }) => !rateId?.trim(),
  ).length;
  const customFrequencyError = areAllOfferingsOneTime(watchOfferings)
    ? null
    : getCustomFrequencyError(watchBillingFrequency, watchCustomBillingFreq);

  const getReviewFooter = () => {
    if (isAddRunStarted && !isBulkOfferingLoading) {
      return {
        next: {
          label: `Retry ${failedCount} failed`,
          testId: 'bulk-offering-retry-btn',
          hidden: !failedCount,
          onClick: handleRetrySubmit,
        },
        skip: {
          label: 'Done',
          testId: 'bulk-offering-done-btn',
          onClick: (_: unknown, resetStepper?: () => void) =>
            resetFlow(resetStepper),
        },
      };
    }

    let note: ReactNode = null;
    if (customFrequencyError) {
      note = (
        <MText fontSize="xs" color="action.danger">
          {customFrequencyError}
        </MText>
      );
    } else if (missingRateCount > 0) {
      note = (
        <MText fontSize="xs" color="fg.muted">
          {missingRateCount} {pluralize('offering', missingRateCount)} still{' '}
          {missingRateCount === 1 ? 'needs' : 'need'} a rate
        </MText>
      );
    }

    return {
      prev: {
        label: 'Back',
        testId: 'bulk-offering-back-btn',
        onClick: (movePrev: () => void) => movePrev(),
        disable: isBulkOfferingLoading,
      },
      next: {
        label: getAddLabel(bulkRateWatch.length),
        testId: 'bulk-offering-add-btn',
        loading: isBulkOfferingLoading,
        onClick: handleAddSubmit,
        disable:
          !watchOfferings.length ||
          missingRateCount > 0 ||
          !!customFrequencyError,
      },
      note: isBulkOfferingLoading ? null : note,
    };
  };

  return (
    <StepperComponent
      ref={stepperRef}
      content={[
        {
          label: 'Select Offerings',
          item: (
            <BulkOfferingSelection
              displayImportOffering={displayImportOffering}
              quote={quote}
              onImported={quoteData.setQuote}
              disabled={disabled}
              control={control}
              errors={errors}
              items={items}
              onApply={handleOnApply}
            />
          ),
          footer: {
            // The picker's own "Review rates" button triggers this
            next: {
              label: 'Next',
              hidden: true,
              onClick: (moveNext: () => void) => {
                trigger('offerings').then((isValid) => {
                  if (!isValid) {
                    return;
                  }
                  const billingFrequency = getCachedBillingFrequency();
                  setValue('billingFrequency', billingFrequency);
                  setValue('customBillingFrequency', null);
                  setValue(
                    'bulkRate',
                    resolveBulkRates(
                      getValues('offerings'),
                      billingFrequency,
                      getValues('bulkRate'),
                    ),
                  );
                  moveNext();
                });
              },
            },
          },
        },
        {
          label: isAddRunStarted ? (
            <BulkOfferingAddStatusHeader
              addStatuses={addStatuses}
              isRunning={isBulkOfferingLoading}
              runSize={runSize}
            />
          ) : (
            'Review rates'
          ),
          item: isAddRunStarted ? (
            <BulkOfferingAddStatusList
              bulkRate={bulkRateWatch}
              offerings={watchOfferings}
              addStatuses={addStatuses}
            />
          ) : (
            <BulkReviewRates
              control={control}
              setValue={setValue}
              getValues={getValues}
              watch={watch}
              disabled={disabled}
            />
          ),
          footer: getReviewFooter(),
        },
      ]}
      defaultIndex={0}
    />
  );
};
